import React, { useState } from 'react';
import {
  Box, Button, Container, FormControl, FormLabel, 
  Heading, Input, VStack, Textarea, Switch, FormHelperText,
  HStack, useToast, Card, CardBody, CardHeader, 
  Text, Icon, Divider, Flex, Badge, InputGroup,
  InputLeftElement, InputRightElement, IconButton,
  useColorModeValue, SimpleGrid, Stack, Progress,
  Alert, AlertIcon, Tooltip, FormErrorMessage
} from '@chakra-ui/react';
import { 
  ArrowBackIcon, 
  InfoIcon, 
  CheckIcon, 
  CopyIcon,
  ViewIcon,
  EditIcon,
  LinkIcon
} from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiClient from '../../../../utils/apiClient';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const TaxonomyCreate = () => {
  const navigate = useNavigate();
  const toast = useToast();
  
  // Color mode values - moved to top level to follow rules of hooks
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const progressBg = useColorModeValue('gray.200', 'gray.700');
  
  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0] || null;

  // Form state
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    tenant_id: tenantId,
    name: '',
    slug: '',
    description: '',
    hierarchical: false,
    multiple_selection: true
  });

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle switch changes
  const handleSwitchChange = (name) => (e) => {
    setFormData(prev => ({
      ...prev,
      [name]: e.target.checked
    }));
  };

  // Generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  // Auto-generate slug when name changes
  const handleNameChange = (e) => {
    const name = e.target.value;
    const slug = generateSlug(name);
    
    setFormData(prev => ({
      ...prev,
      name,
      slug
    }));
    
    // Clear name error
    if (errors.name) {
      setErrors(prev => ({ ...prev, name: '' }));
    }
  };

  // Copy slug to clipboard
  const copySlug = () => {
    navigator.clipboard.writeText(formData.slug);
    toast({
      title: 'Copied!',
      description: 'Slug copied to clipboard',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Taxonomy name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    if (!tenantId) {
      toast({
        title: 'Authentication Error',
        description: 'No tenant ID found. Please log in again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoading(true);
      setCurrentStep(2);
      
      // Simulate progress
      setTimeout(() => setCurrentStep(3), 500);
      
      const payload = {
        tenant_id: tenantId,
        name: formData.name,
        slug: formData.slug || generateSlug(formData.name)
      };

      await apiClient('/taxonomies', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      setCurrentStep(4);
      
      toast({
        title: 'Success!',
        description: "Your taxonomy has been created successfully.",
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Delay navigation to show success state
      setTimeout(() => {
        navigate('/taxonomies');
      }, 1000);
      
    } catch (error) {
      console.error('Error creating taxonomy:', error);
      setCurrentStep(1);
      toast({
        title: 'Creation Failed',
        description: error.message || 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Progress calculation
  const progress = ((currentStep - 1) / 3) * 100;

  return (
    <Box minH="100vh" bg={bgColor} py={8}>
      <Container maxW="container.lg">
        {/* Header */}
        <MotionBox
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Button 
            leftIcon={<ArrowBackIcon />} 
            variant="ghost" 
            mb={6}
            as={RouterLink}
            to="/taxonomies"
            size="lg"
          >
            Back to Taxonomies
          </Button>
          
          <Flex align="center" justify="space-between" mb={8}>
            <Box>
              <Heading size="xl" mb={2}>Create Taxonomy</Heading>
              <Text color={textColor} fontSize="lg">
                Organize your content with custom taxonomies
              </Text>
            </Box>
            <Badge colorScheme="blue" fontSize="md" p={2} borderRadius="md">
              New
            </Badge>
          </Flex>
        </MotionBox>

        {/* Progress Bar */}
        {isLoading && (
          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            mb={6}
          >
            <Box mb={2}>
              <Text fontSize="sm" color={textColor}>
                {currentStep === 1 && "Preparing..."}
                {currentStep === 2 && "Validating data..."}
                {currentStep === 3 && "Creating taxonomy..."}
                {currentStep === 4 && "Success!"}
              </Text>
            </Box>
            <Progress 
              value={progress} 
              colorScheme="blue" 
              borderRadius="md"
              bg={progressBg}
            />
          </MotionBox>
        )}

        {/* Main Form */}
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8}>
          {/* Form Column */}
          <Box gridColumn={{ base: 1, lg: "1 / 3" }}>
            <MotionCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              bg={cardBg}
              borderColor={borderColor}
              shadow="xl"
            >
              <CardHeader>
                <Heading size="md">Basic Information</Heading>
                <Text color={textColor} mt={1}>
                  Define the core properties of your taxonomy
                </Text>
              </CardHeader>
              <CardBody>
                <Box as="form" onSubmit={handleSubmit}>
                  <VStack spacing={6} align="stretch">
                    {/* Name Field */}
                    <FormControl isRequired isInvalid={!!errors.name}>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        <Flex align="center">
                          <EditIcon mr={2} />
                          Taxonomy Name
                        </Flex>
                      </FormLabel>
                      <Input 
                        name="name" 
                        value={formData.name}
                        onChange={handleNameChange}
                        placeholder="e.g., Categories, Tags, Locations" 
                        size="lg"
                        focusBorderColor="blue.500"
                      />
                      <FormHelperText>
                        A human-readable name that describes this taxonomy
                      </FormHelperText>
                      <FormErrorMessage>{errors.name}</FormErrorMessage>
                    </FormControl>
                    
                    {/* Slug Field */}
                    <FormControl isInvalid={!!errors.slug}>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        <Flex align="center">
                          <LinkIcon mr={2} />
                          URL Slug
                        </Flex>
                      </FormLabel>
                      <InputGroup size="lg">
                        <InputLeftElement pointerEvents="none">
                          <Text fontSize="sm" color={textColor}>/</Text>
                        </InputLeftElement>
                        <Input 
                          name="slug"
                          value={formData.slug}
                          onChange={handleChange}
                          placeholder="taxonomy-slug"
                          focusBorderColor="blue.500"
                        />
                        <InputRightElement>
                          <Tooltip label="Copy slug">
                            <IconButton
                              icon={<CopyIcon />}
                              size="sm"
                              variant="ghost"
                              onClick={copySlug}
                              isDisabled={!formData.slug}
                            />
                          </Tooltip>
                        </InputRightElement>
                      </InputGroup>
                      <FormHelperText>
                        URL-friendly identifier (auto-generated from name)
                      </FormHelperText>
                      <FormErrorMessage>{errors.slug}</FormErrorMessage>
                    </FormControl>
                    
                    {/* Description Field */}
                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        Description (Optional)
                      </FormLabel>
                      <Textarea 
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Describe the purpose and usage of this taxonomy" 
                        rows={4}
                        resize="vertical"
                        focusBorderColor="blue.500"
                      />
                      <FormHelperText>
                        Help others understand when and how to use this taxonomy
                      </FormHelperText>
                    </FormControl>
                    
                    <Divider />
                    
                    {/* Advanced Options */}
                    <Box>
                      <Text fontSize="lg" fontWeight="semibold" mb={4}>
                        Advanced Options
                      </Text>
                      
                      <Stack spacing={4}>
                        <FormControl>
                          <Flex justify="space-between" align="center">
                            <Box>
                              <FormLabel mb={1} fontSize="md" fontWeight="semibold">
                                Hierarchical Structure
                              </FormLabel>
                              <FormHelperText>
                                Allow terms to have parent-child relationships (like categories)
                              </FormHelperText>
                            </Box>
                            <Switch 
                              colorScheme="blue"
                              size="lg"
                              isChecked={formData.hierarchical}
                              onChange={handleSwitchChange('hierarchical')}
                            />
                          </Flex>
                        </FormControl>
                        
                        <FormControl>
                          <Flex justify="space-between" align="center">
                            <Box>
                              <FormLabel mb={1} fontSize="md" fontWeight="semibold">
                                Multiple Selection
                              </FormLabel>
                              <FormHelperText>
                                Content can be assigned multiple terms from this taxonomy
                              </FormHelperText>
                            </Box>
                            <Switch 
                              colorScheme="blue"
                              size="lg"
                              isChecked={formData.multiple_selection}
                              onChange={handleSwitchChange('multiple_selection')}
                            />
                          </Flex>
                        </FormControl>
                      </Stack>
                    </Box>
                    
                    {/* Action Buttons */}
                    <HStack w="100%" justify="flex-end" pt={6} spacing={4}>
                      <Button 
                        variant="outline" 
                        size="lg"
                        as={RouterLink} 
                        to="/taxonomies"
                        isDisabled={isLoading}
                      >
                        Cancel
                      </Button>
                      <Button 
                        colorScheme="blue" 
                        type="submit"
                        size="lg"
                        isLoading={isLoading}
                        loadingText={
                          currentStep === 2 ? "Validating..." :
                          currentStep === 3 ? "Creating..." : "Processing..."
                        }
                        isDisabled={!formData.name || !tenantId}
                        leftIcon={!isLoading ? <CheckIcon /> : undefined}
                      >
                        Create Taxonomy
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              </CardBody>
            </MotionCard>
          </Box>

          {/* Info Sidebar */}
          <Box>
            <VStack spacing={6} align="stretch">
              {/* Tips Card */}
              <MotionCard
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                bg={cardBg}
                borderColor={borderColor}
              >
                <CardHeader>
                  <Flex align="center">
                    <Icon as={InfoIcon} color="blue.500" mr={2} />
                    <Heading size="sm">Tips</Heading>
                  </Flex>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={3}>
                    <Text fontSize="sm" color={textColor}>
                      <Text as="span" fontWeight="semibold">Names:</Text> Use clear, descriptive names like "Product Categories" or "Article Tags"
                    </Text>
                    <Text fontSize="sm" color={textColor}>
                      <Text as="span" fontWeight="semibold">Hierarchical:</Text> Enable for category-like structures where terms can have subcategories
                    </Text>
                    <Text fontSize="sm" color={textColor}>
                      <Text as="span" fontWeight="semibold">Multiple Selection:</Text> Allow content to belong to multiple terms (usually enabled)
                    </Text>
                  </VStack>
                </CardBody>
              </MotionCard>

              {/* Preview Card */}
              {formData.name && (
                <MotionCard
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  bg={cardBg}
                  borderColor={borderColor}
                >
                  <CardHeader>
                    <Flex align="center">
                      <Icon as={ViewIcon} color="green.500" mr={2} />
                      <Heading size="sm">Preview</Heading>
                    </Flex>
                  </CardHeader>
                  <CardBody pt={0}>
                    <VStack align="stretch" spacing={3}>
                      <Box>
                        <Text fontSize="xs" color={textColor} textTransform="uppercase" letterSpacing="wide">
                          Display Name
                        </Text>
                        <Text fontWeight="semibold">{formData.name}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} textTransform="uppercase" letterSpacing="wide">
                          URL Slug
                        </Text>
                        <Text fontFamily="mono" fontSize="sm" color="blue.500">
                          /{formData.slug}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} textTransform="uppercase" letterSpacing="wide">
                          Configuration
                        </Text>
                        <VStack align="stretch" spacing={1}>
                          <Flex justify="space-between">
                            <Text fontSize="sm">Hierarchical</Text>
                            <Badge colorScheme={formData.hierarchical ? 'green' : 'gray'} size="sm">
                              {formData.hierarchical ? 'Yes' : 'No'}
                            </Badge>
                          </Flex>
                          <Flex justify="space-between">
                            <Text fontSize="sm">Multiple Terms</Text>
                            <Badge colorScheme={formData.multiple_selection ? 'green' : 'gray'} size="sm">
                              {formData.multiple_selection ? 'Yes' : 'No'}
                            </Badge>
                          </Flex>
                        </VStack>
                      </Box>
                    </VStack>
                  </CardBody>
                </MotionCard>
              )}

              {/* Success Message */}
              {currentStep === 4 && (
                <MotionBox
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="semibold">Taxonomy Created!</Text>
                      <Text fontSize="sm">Redirecting to taxonomy list...</Text>
                    </Box>
                  </Alert>
                </MotionBox>
              )}
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default TaxonomyCreate;
